import React, { FC, useEffect, useMemo } from "react";
import styles from "./Profile.module.scss";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import apiInstance from "../services/httpService";
import Loader from "../Loader/Loader";

const FIELD_LABELS: Record<string, string> = {
  name: "Full Name",
  phone: "Phone",
  phone_2: "WhatsApp Number",
  address: "Address",
  city: "City",
  state: "State",
  country: "Country",
  pincode: "Pincode",
};

const EDITABLE_FIELDS = [
  "name", "phone", "phone_2",
  "address", "city", "state", "country", "pincode",
];

function levenshtein(a: string, b: string): number {
  const m = a.length, n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }
  return dp[m][n];
}

function isDrasticNameChange(original: string, newName: string): boolean {
  const a = original.toLowerCase().trim().replace(/\s+/g, " ");
  const b = newName.toLowerCase().trim().replace(/\s+/g, " ");
  if (a === b) return false;

  if (a.includes(b) || b.includes(a)) return false;

  const wordsA = a.split(" ").filter((w) => w.length > 1);
  const wordsB = b.split(" ").filter((w) => w.length > 1);

  if (wordsA.some((w) => wordsB.includes(w))) return false;

  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return false;
  const similarity = 1 - levenshtein(a, b) / maxLen;
  if (similarity > 0.4) return false;

  return true;
}

interface ProfileProps {}

const Profile: FC<ProfileProps> = () => {
  const [profile, setProfile] = React.useState<any>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [editedValues, setEditedValues] = React.useState<Record<string, string>>({});
  const [editableFields, setEditableFields] = React.useState<Set<string>>(new Set());
  const [saving, setSaving] = React.useState(false);

  // Drastic name confirmation (pre-save)
  const [nameConfirm, setNameConfirm] = React.useState<{
    open: boolean;
  }>({ open: false });

  // Main save confirmation showing old vs new
  const [saveConfirm, setSaveConfirm] = React.useState<{
    open: boolean;
    changes: { field: string; oldVal: string; newVal: string }[];
  }>({ open: false, changes: [] });

  const [snackbar, setSnackbar] = React.useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const Alert = React.forwardRef(function Alert(props: any, ref: any) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
  });

  useEffect(() => {
    const token = localStorage.getItem("token");
    apiInstance
      .axiosCall("api/ccc/user/profile", { token }, "POST")
      .then((response: any) => {
        if (response.data.status === "success") {
          setProfile(response.data.data);
        } else {
          setError(response.data.message || "Could not load profile. Please try again.");
        }
        setIsLoading(false);
      })
      .catch(() => {
        setError("Could not load profile. Please try again.");
        setIsLoading(false);
      });
  }, []);

  // Once profile loads, seed editedValues with original values
  useEffect(() => {
    if (profile) {
      const initial: Record<string, string> = {};
      EDITABLE_FIELDS.forEach((f) => {
        initial[f] = profile[f] || "";
      });
      setEditedValues(initial);
    }
  }, [profile]);

  const dirtyFields = useMemo(() => {
    if (!profile) return [];
    return EDITABLE_FIELDS.filter((f) => {
      const original = (profile[f] || "").trim();
      const current = (editedValues[f] || "").trim();
      return original !== current;
    });
  }, [profile, editedValues]);

  const hasDirty = dirtyFields.length > 0;

  const handleEditClick = (field: string) => {
    setEditableFields((prev) => new Set(prev).add(field));
  };

  const handleRevertField = (field: string) => {
    setEditedValues((prev) => ({ ...prev, [field]: profile?.[field] || "" }));
    setEditableFields((prev) => {
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };

  const handleValueChange = (field: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [field]: value }));
  };

  // Step 1: check drastic name change, then open save confirmation
  const handleSaveClick = () => {
    const nameDirty = dirtyFields.includes("name");
    const newName = (editedValues.name || "").trim();
    const oldName = (profile.name || "").trim();

    if (nameDirty && isDrasticNameChange(oldName, newName)) {
      setNameConfirm({ open: true });
      return;
    }

    openSaveConfirm();
  };

  // After drastic name is acknowledged, proceed to save confirmation
  const handleNameConfirmProceed = () => {
    setNameConfirm({ open: false });
    openSaveConfirm();
  };

  const handleNameConfirmCancel = () => {
    setNameConfirm({ open: false });
  };

  const openSaveConfirm = () => {
    const changes = dirtyFields.map((f) => ({
      field: FIELD_LABELS[f] || f,
      oldVal: profile[f] || "",
      newVal: editedValues[f] || "",
    }));
    setSaveConfirm({ open: true, changes });
  };

  const handleSaveConfirmCancel = () => {
    setSaveConfirm({ open: false, changes: [] });
  };

  // Final save to backend
  const handleSaveConfirmProceed = () => {
    try {
      setSaveConfirm({ open: false, changes: [] });

      const token = localStorage.getItem("token");
      if (!token) {
        setSnackbar({ open: true, message: "You must be logged in.", severity: "error" });
        return;
      }

      const changedData: Record<string, string> = {};
      EDITABLE_FIELDS.forEach((f) => {
        const cur = (editedValues[f] || "").trim();
        const orig = (profile ? profile[f] || "" : "").trim();
        if (cur !== orig) {
          changedData[f] = cur;
        }
      });

      console.log("Saving changedData:", JSON.stringify(changedData));

      setSaving(true);

      apiInstance
        .axiosCall("api/ccc/user/update/user_bio", { token, ...changedData }, "POST")
        .then((response: any) => {
          setSaving(false);
          if (response?.data?.status === "success") {
            setEditableFields(new Set());
            return apiInstance
              .axiosCall("api/ccc/user/profile", { token }, "POST")
              .then((fresh: any) => {
                if (fresh?.data?.status === "success") {
                  setProfile(fresh.data.data);
                }
                setSnackbar({ open: true, message: response.data.message || "Profile updated successfully", severity: "success" });
              });
          }
          setSnackbar({ open: true, message: response.data?.message || "Update failed", severity: "error" });
        })
        .catch((err: any) => {
          setSaving(false);
          const detail =
            err?.response?.data?.message ||
            err?.message ||
            "Update failed. Please try again.";
          setSnackbar({ open: true, message: detail, severity: "error" });
          console.error("Profile update error:", err);
        });
    } catch (err: any) {
      setSaving(false);
      setSnackbar({ open: true, message: err?.message || "Unexpected error", severity: "error" });
      console.error("Profile update unexpected error:", err);
    }
  };

  const renderField = (field: string, gridClass: string) => {
    const isEditing = editableFields.has(field);
    const value = editedValues[field] ?? "";

    const adornment = isEditing ? (
      <InputAdornment position="end" className={styles.Profile__adornment}>
        <IconButton size="small" onClick={() => handleRevertField(field)} edge="end">
          <CloseIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    ) : (
      <InputAdornment position="end" className={styles.Profile__adornment}>
        <IconButton size="small" onClick={() => handleEditClick(field)} edge="end">
          <EditIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    );

    const handleBlur = () => {
      const original = (profile?.[field] || "").trim();
      const current = (editedValues[field] || "").trim();
      if (original === current) {
        setEditableFields((prev) => {
          const next = new Set(prev);
          next.delete(field);
          return next;
        });
      }
    };

    return (
      <div className={`${gridClass} my-2`} key={field}>
        <TextField
          fullWidth
          label={FIELD_LABELS[field] || field}
          value={value}
          disabled={!isEditing}
          onChange={(e) => handleValueChange(field, e.target.value)}
          onBlur={isEditing ? handleBlur : undefined}
          variant="outlined"
          size="small"
          slotProps={{
            input: { endAdornment: adornment },
          }}
        />
      </div>
    );
  };

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <div className="row col-lg-10 mx-auto my-4">
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className={"row col-lg-10 mx-auto my-4 " + styles.Profile}>
      <div className={styles.Profile__header}>
        <h3 className={styles.Profile__title}>My Profile</h3>
        <Button
          variant="contained"
          disabled={!hasDirty || saving}
          onClick={handleSaveClick}
          className={styles.Profile__save_btn}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </div>

      {/* Personal Details */}
      <div className={styles.Profile__section_label}>Personal Details</div>

      {renderField("name", "col-lg-6")}
      <div className="col-lg-6 my-2">
        <TextField
          fullWidth
          label="Email"
          value={profile.email || ""}
          disabled
          variant="outlined"
          size="small"
        />
      </div>
      <div className="col-lg-6 my-2">
        <TextField
          fullWidth
          label="Date of Birth"
          value={profile.dob || ""}
          disabled
          variant="outlined"
          size="small"
        />
      </div>
      <div className="col-lg-6 my-2">
        <TextField
          fullWidth
          label="Age"
          value={profile.age || ""}
          disabled
          variant="outlined"
          size="small"
        />
      </div>
      <div className="col-lg-6 my-2">
        <TextField
          fullWidth
          label="Gender"
          value={profile.gender || ""}
          disabled
          variant="outlined"
          size="small"
        />
      </div>
      <div className="col-lg-6 my-2">
        <TextField
          fullWidth
          label="Choir Part"
          value={profile.vocal_part || ""}
          disabled
          variant="outlined"
          size="small"
        />
      </div>

      {/* Contact Details */}
      <div className={styles.Profile__section_label}>Contact Details</div>

      {renderField("phone", "col-lg-6")}
      {renderField("phone_2", "col-lg-6")}

      {/* Address */}
      <div className={styles.Profile__section_label}>Address</div>

      {renderField("address", "col-lg-12")}
      {renderField("city", "col-lg-6")}
      {renderField("state", "col-lg-6")}
      {renderField("country", "col-lg-6")}
      {renderField("pincode", "col-lg-6")}

      {/* Drastic name change confirmation */}
      <Dialog open={nameConfirm.open} onClose={handleNameConfirmCancel}>
        <DialogTitle>Confirm Name Change</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are about to change your name from{" "}
            <strong>{profile.name}</strong> to{" "}
            <strong>{editedValues.name}</strong>. This is a significant change.
            Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleNameConfirmCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleNameConfirmProceed} color="primary" variant="contained">
            Yes, Change Name
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save confirmation with old vs new values */}
      <Dialog open={saveConfirm.open} onClose={handleSaveConfirmCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Changes</DialogTitle>
        <DialogContent>
          <div className={styles.Profile__changes_list}>
            {saveConfirm.changes.map((c, i) => (
              <div key={i} className={styles.Profile__change_row}>
                <span className={styles.Profile__change_field}>{c.field}</span>
                <span className={styles.Profile__change_old}>{c.oldVal}</span>
                <span className={styles.Profile__change_arrow}>&rarr;</span>
                <span className={styles.Profile__change_new}>{c.newVal}</span>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSaveConfirmCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSaveConfirmProceed} color="primary" variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default Profile;
